import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiPhone, FiMapPin, FiClock, FiStar, FiAward, FiHeart, FiCheck, FiThumbsUp} = FiIcons;

const Home = () => {
  const features = [
    {
      icon: FiAward,
      title: 'Premium Quality',
      description: 'Fresh ingredients and traditional recipes ensure the highest quality baked goods.'
    },
    {
      icon: FiHeart,
      title: 'Made with Love',
      description: 'Every item is crafted with care and attention to detail by our skilled bakers.'
    },
    {
      icon: FiStar,
      title: 'Authentic Taste',
      description: 'Traditional Chinese bakery items with authentic flavors and techniques.'
    }
  ];

  const testimonials = [
    {
      name: "Sarah Jenkins",
      text: "The best Chinese bakery in Toronto! Their egg tarts are absolutely legendary - flaky crust and perfect custard. A must-visit on Broadview.",
      rating: 5
    },
    {
      name: "Michael Chen",
      text: "I've been coming here for years. The BBQ pork buns are always fresh and soft. It brings back so many childhood memories.",
      rating: 5
    },
    {
      name: "Emily Wong",
      text: "The variety is amazing and everything is so affordable. The staff is always friendly even when it's busy. Highly recommended!",
      rating: 5
    }
  ];

  const signatureItems = [
    {
      title: "Hong Kong Style Egg Tarts",
      desc: "Our famous egg tarts feature a buttery, flaky crust filled with smooth, creamy egg custard. Baked fresh throughout the day.",
      image: "https://i.ibb.co/cS2SVsW3/images-1.jpg"
    },
    {
      title: "BBQ Pork Buns (Cha Siu Bao)",
      desc: "Soft, fluffy steamed or baked buns filled with our secret recipe sweet and savory BBQ pork filling.",
      image: "https://i.ibb.co/xt4fzht6/images-2.jpg"
    },
    {
      title: "Pineapple Buns (Bo Lo Bao)",
      desc: "A classic favorite! Soft sweet bun topped with a crunchy, crumbly sugar crust that resembles a pineapple texture.",
      image: "https://i.ibb.co/zhg375yy/images-3.jpg"
    },
    {
      title: "Coconut Cream Buns",
      desc: "Delicate sweet buns split and filled with fresh whipped cream and topped with shredded coconut.",
      image: "https://i.ibb.co/N61r4P60/images-4.jpg"
    }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen">
      {/* Hero Section - Split layout with frame */}
      <section className="relative bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] py-20 overflow-hidden">
        {/* Red circle templates - 30% lighter */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            initial={{ scale: 0, rotate: 0 }}
            animate={{ scale: 1, rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute -top-20 -right-20 w-80 h-80 bg-[#ff8080] rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            initial={{ scale: 0, rotate: 0 }}
            animate={{ scale: 1, rotate: -360 }}
            transition={{ duration: 25, repeat: Infinity, repeatType: "reverse" }}
            className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#ffcc80] rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            initial={{ y: -100 }}
            animate={{ y: 100 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-20 left-1/4 w-12 h-12 bg-[#ff6666] rounded-full opacity-30"
          />
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: -100 }}
            transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-1/3 w-8 h-8 bg-[#ffb366] rounded-full opacity-40"
          />
          <motion.div
            initial={{ x: -50 }}
            animate={{ x: 50 }}
            transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/3 right-1/4 w-6 h-6 bg-[#ff4d4d] rounded-full opacity-50"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div 
              initial={{ x: -30, opacity: 0 }} 
              animate={{ x: 0, opacity: 1 }} 
              transition={{ delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <motion.h1
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-4xl md:text-6xl font-bold mb-6 text-black"
              >
                Tung Hing Bakery
              </motion.h1>
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto lg:mx-0 text-black font-medium"
              >
                Fresh Baked Goods & Traditional Chinese Pastries in Toronto
              </motion.p>
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link to="/menu-gallery" className="bg-red-500 text-black px-8 py-3 rounded-lg font-semibold hover:bg-red-600 transition-all transform hover:scale-105 shadow-lg">
                  View Our Gallery
                </Link>
                <Link to="/contact" className="bg-yellow-400 text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-500 transition-all transform hover:scale-105 shadow-lg">
                  Contact Us
                </Link>
              </motion.div>
            </motion.div>

            {/* Right side - Image frame */}
            <motion.div 
              initial={{ x: 30, opacity: 0 }} 
              animate={{ x: 0, opacity: 1 }} 
              transition={{ delay: 0.3 }}
              className="relative"
            >
              {/* Frame container */}
              <div className="relative bg-white rounded-2xl shadow-2xl p-4 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                {/* Inner frame */}
                <div className="relative bg-gray-100 rounded-xl p-2 transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                  {/* Image grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative overflow-hidden rounded-lg">
                      <img 
                        src="https://i.ibb.co/Gf15ZrHT/images-7.jpg" 
                        alt="Bakery items 1" 
                        className="w-full h-32 object-cover transform hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="relative overflow-hidden rounded-lg">
                      <img 
                        src="https://i.ibb.co/wF67K2K5/images-13.jpg" 
                        alt="Bakery items 2" 
                        className="w-full h-32 object-cover transform hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="relative overflow-hidden rounded-lg">
                      <img 
                        src="https://i.ibb.co/cS2SVsW3/images-1.jpg" 
                        alt="Egg tarts" 
                        className="w-full h-32 object-cover transform hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="relative overflow-hidden rounded-lg">
                      <img 
                        src="https://i.ibb.co/xt4fzht6/images-2.jpg" 
                        alt="BBQ buns" 
                        className="w-full h-32 object-cover transform hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -top-4 -right-4 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
              >
                <SafeIcon icon={FiStar} className="w-6 h-6 text-white" />
              </motion.div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.9, duration: 0.5 }}
                className="absolute -bottom-4 -left-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg"
              >
                <SafeIcon icon={FiHeart} className="w-6 h-6 text-black" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Welcome Message */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Welcome to Tung Hing Bakery</h2>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Located in the heart of Toronto, Tung Hing Bakery has been serving the community with fresh, authentic Chinese baked goods and traditional pastries. Our skilled bakers use time-honored recipes and the finest ingredients to create delicious treats that bring families together.
              </p>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                From fluffy steamed buns to crispy egg tarts, every item is made fresh daily with love and attention to detail. We take pride in maintaining the authentic flavors and quality that our customers have come to expect.
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-red-500 mr-2" />
                  <span className="text-gray-700">Daily Fresh Baking</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-red-500 mr-2" />
                  <span className="text-gray-700">Traditional Family Recipes</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-red-500 mr-2" />
                  <span className="text-gray-700">Premium Ingredients</span>
                </div>
              </div>
              <Link to="/about" className="bg-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors">
                Learn More About Us
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src="https://i.ibb.co/cS2SVsW3/images-1.jpg" alt="Bakery items" className="rounded-lg shadow-lg" />
              <img src="https://i.ibb.co/xt4fzht6/images-2.jpg" alt="Fresh baked goods" className="rounded-lg shadow-lg mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Signature Items Section */}
      <section className="py-16 bg-red-50 relative overflow-hidden">
        {/* Moving elements - 30% lighter */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 100, opacity: 0.3 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-16 h-16 bg-[#ff8080] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-20 w-20 h-20 bg-[#ffcc80] rounded-full blur-xl"
          />
          <motion.div
            initial={{ rotate: 0, scale: 0.8 }}
            animate={{ rotate: 360, scale: 1 }}
            transition={{ duration: 25, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/2 left-1/4 w-24 h-24 bg-[#ff9999] rounded-full opacity-20 blur-2xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Signature Delights</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Discover the customer favorites that have made Tung Hing Bakery a local landmark.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {signatureItems.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/menu-gallery" className="inline-flex items-center text-red-500 font-semibold hover:text-red-600">
              See All Items <SafeIcon icon={FiThumbsUp} className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Moving elements - 30% lighter */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: -100, opacity: 0.3 }}
            transition={{ duration: 18, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/4 right-10 w-14 h-14 bg-[#ff9999] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: -80, opacity: 0.3 }}
            transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-10 left-1/3 w-18 h-18 bg-[#ff8080] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Don't just take our word for it. Here's what our community loves about Tung Hing Bakery.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((review, index) => (
              <div key={index} className="bg-red-50 p-6 rounded-lg shadow-sm border border-red-100">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{review.text}"</p>
                <p className="font-bold text-gray-900">- {review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <SafeIcon icon={FiPhone} className="w-8 h-8 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Call Us</h3>
              <p className="text-gray-600">+14164659103</p>
            </div>
            <div className="text-center">
              <SafeIcon icon={FiMapPin} className="w-8 h-8 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Visit Us</h3>
              <p className="text-gray-600">353 Broadview Ave, Toronto, ON</p>
            </div>
            <div className="text-center">
              <SafeIcon icon={FiClock} className="w-8 h-8 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Hours</h3>
              <p className="text-gray-600">Daily: 7:00 AM - 8:00 PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Tung Hing Bakery?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We're committed to providing the best bakery experience with quality, authenticity, and exceptional service.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index} 
                initial={{ y: 30, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: index * 0.2 }} 
                className="text-center bg-white p-8 rounded-lg shadow-lg"
              >
                <SafeIcon icon={feature.icon} className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ scale: 0.5, rotate: 0 }}
            animate={{ scale: 1, rotate: 180 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-32 h-32 bg-[#ff6666] rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: -50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-1/4 w-16 h-16 bg-[#ffcc80] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Taste the Difference Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Visit our bakery and experience the authentic flavors of traditional Chinese pastries and fresh baked goods made with love.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/menu-gallery" className="bg-red-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors">
              View Our Menu
            </Link>
            <a href="tel:+14164659103" className="border-2 border-red-600 text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-red-600 hover:text-white transition-colors">
              Call Now
            </a>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Home;